// CalendarBox.tsx with drop functionality added
import styled from "styled-components";
import { useState, useEffect, useRef, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import BlockIcon from "@mui/icons-material/Block";
import { useSignalEffect } from "@preact/signals-react";
import { refreshVehicleScheduleById } from "../staff_planner/signalStore";
import { useQueryClient } from "react-query";
import {
  useFetchEntities,
  useAddOrDeleteVehicleIdToTourGroup,
  useGetScheduleOfVehicleByDate,
} from "@/reactQueryHooks";
import { useAxios } from "@/axiosProvider";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

//------------------------------------------------------------

interface TourGroup {
  _id: string;
  product_id: string;
  option_id: string;
  start_time_id: string;
  vehicle_id: string;
  index: number;
  //is_projected_vehicle?: boolean;
}

interface Role {
  _id: string;
  title: string;
}

interface Vehicle {
  _id: string;
  plate: string;
}

interface Product {
  _id: string;
  title: string;
  options: Option[];
  start_times?: StartTime[];
}

interface Option {
  _id: string;
  title: string;
}

interface StartTime {
  _id: string;
  time_slot: string;
}

interface Entities {
  data: {
    products: Product[];
    roles: Role[];
    vehicles: Vehicle[];
    staff: Staff[];
  };
}

interface Staff {
  _id: string;
  name: string;
  roles: { _id: string }[];
  value: string;
}

interface ScheduleData {
  _id?: string;
  vehicle_id?: string;
}

interface ContainerProps {
  isDragOver?: boolean;
  canDrop?: boolean;
}

interface User {
  value: string;
  isAdmin?: boolean;
  permissions?: {
    assignSchedule?: boolean;
  };
}

interface CalendarBoxProps {
  date: string;
  vehicle_id: string;
  schedule: ScheduleData[] | null;
}

interface DragSourceData {
  date: string;
  roleId: string;
  tourGroupId: string;
  [key: string]: unknown; // Allow for additional properties
}

//------------------------------------------------------------

const ScheduleItemContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 38px;
  border-radius: 6px;
  //border: 2px solid orange;
  align-items: center;
  color: #3d3d3d;
  background: #dff0f4;
  padding: 3px;
  gap: 2px;
  .title {
    font-size: 10px;
    text-align: center;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  font-weight: bold;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.2);

  .resources-row {
    width: 100%;
    background: whitesmoke;
    border-radius: 3px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    i {
      font-size: 10px;
      margin-right: 2px;
    }
  }
`;

const CommentItemContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border-radius: 3px;
  padding: 0 7px;
  min-height: 35px;
  gap: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f2c571;
  font-size: 11px;
  color: #3d3d3d;
  font-style: italic;
  user-select: none;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.2);
`;

// New styled component for Request Item with a bright, eye-catching color
const RequestItemContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border-radius: 3px;
  padding: 0 7px;
  min-height: 35px;
  gap: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #d166b1; /* Bright coral/red color for high visibility */
  font-size: 11px;
  color: white; /* White text for better contrast */
  font-weight: 600;
  user-select: none;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
  //border-left: 3px solid #e02e33; /* Darker border for emphasis */
`;

const Container = styled.div<ContainerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 5px;
  cursor: pointer;
  justify-content: flex-start;
  overflow: auto;
  gap: 5px;
  height: 100%;
  width: 100%;
  color: white;
  .fa-plus-circle {
    color: #a3a3a3;
    font-size: 20px;
  }

  background: ${({ isDragOver, canDrop }) => {
    if (isDragOver && canDrop) return "green";
    if (isDragOver && !canDrop) return "red";
    return "whitesmoke";
  }};

  &:hover {
    .fa-plus-circle {
      color: ${({ theme }) => theme.colors.green};
    }
    filter: brightness(0.96);

    ${ScheduleItemContainer} {
      background: white;
    }
    ${CommentItemContainer} {
      background: #f2d6a1;
    }
    /* ${RequestItemContainer} {
      background: #ff7478;
    } */
  }
  overflow: auto;
  ::-webkit-scrollbar {
    width: 3px;
  }
  ::-webkit-scrollbar-thumb {
    background: #91c9ff;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: center;
`;

interface OverlayProps {
  allowDrop: boolean;
}

const Overlay = styled.div<OverlayProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  backdrop-filter: blur(6px);
  background: ${({ allowDrop }) =>
    allowDrop ? "rgba(0,255,0,0.2)" : "rgba(255,0,0,0.2)"};
`;

//-----------------------------------------------------------------------------------

const ScheduleItem = ({ tourGroup }: { tourGroup: TourGroup }) => {
  const { data: entities } = useFetchEntities() as { data: Entities };

  const product = entities.data.products.find(
    (product) => product._id === tourGroup?.product_id
  );

  const option = product?.options.find(
    (option) => option._id === tourGroup?.option_id
  );

  const startTime = product?.start_times?.find(
    (start_time) => start_time._id === tourGroup?.start_time_id
  );

  //const isProjectedSchedule = tourGroup?.is_projected_vehicle || false;

  return (
    <ScheduleItemContainer
      title={`${product?.title} - ${option?.title} @ ${startTime?.time_slot}`}
    >
      {/* <Tooltip title={isProjectedSchedule ? "Projected Schedule" : null}>
        <CornerRibbon isProjectedSchedule={isProjectedSchedule} />
      </Tooltip> */}

      {tourGroup ? (
        <>
          <span className="title">{`${product?.title} (${option?.title})`}</span>
          <div className="resources-row">
            <span>
              <i className="fa-solid fa-clock"></i>
              {startTime?.time_slot}
            </span>
            <span>Group #{tourGroup?.index}</span>
          </div>
        </>
      ) : (
        <span
          style={{
            color: "indianred",
            fontSize: "13px",
          }}
        >
          Error getting group
        </span>
      )}
    </ScheduleItemContainer>
  );
};

//-----------------------------------------------------------------------------------

const CalendarBox = ({
  date: calendarDate,
  vehicle_id,
  schedule,
}: CalendarBoxProps) => {
  const queryClient = useQueryClient();
  const { data: entities } = useFetchEntities() as { data: Entities };

  const { mutateAsync: addOrDeleteVehicleIdToTourGroup } =
    useAddOrDeleteVehicleIdToTourGroup() as any;
  const user = entities.data.staff.find((user) => user._id === vehicle_id);
  //const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [scheduleData, setScheduleData] = useState<ScheduleData[] | null>(
    schedule
  );

  const { mutateAsync: getScheduleOfVehicleByDate, isPending } =
    useGetScheduleOfVehicleByDate() as any;

  const [onDrop, setOnDrop] = useState<"allow" | "deny" | null>(null);
  const hasScheduleData = scheduleData !== null;
  const isEmptyOrHasNoData = scheduleData?.length === 0;
  const containerRef = useRef<HTMLDivElement>(null);

  const { user: currentUser } = useAxios() as { user: User };
  const userIsAdmin = currentUser.isAdmin;
  const userIsPermittedToAssignSchedule =
    currentUser.permissions?.assignSchedule || userIsAdmin;

  const shouldShowPlusIcon = isEmptyOrHasNoData;

  const checkIfVehicleHasAlreadyAssignedThisTourGroup = useCallback(
    (tourGroupId: string): boolean => {
      return scheduleData?.some((tg) => tg._id === tourGroupId) || false;
    },
    [scheduleData]
  );

  const getNewSchedule = async () => {
    try {
      const newSchedule = await getScheduleOfVehicleByDate({
        vehicle_id: vehicle_id,
        date: calendarDate,
      });

      if (newSchedule?.data) {
        setScheduleData(newSchedule.data);
      }
    } catch (error) {
      setScheduleData(null);
    } finally {
      refreshVehicleScheduleById.value = null;
    }
  };

  useSignalEffect(() => {
    const date = refreshVehicleScheduleById.value?.date;
    const vehicleId = refreshVehicleScheduleById.value?.vehicleId;
    if (vehicleId === vehicle_id && date === calendarDate) {
      getNewSchedule();
    }
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      onDragEnter: ({ source }) => {
        const { date, tourGroupId } = source.data as unknown as DragSourceData;
        const hasAlreadyAssigned =
          checkIfVehicleHasAlreadyAssignedThisTourGroup(tourGroupId);
        const datesMatch = calendarDate === date;
        const canDrop =
          userIsPermittedToAssignSchedule &&
          datesMatch &&
          !hasAlreadyAssigned &&
          tourGroupId;
        setOnDrop(canDrop ? "allow" : "deny");
      },
      onDragLeave: () => {
        setOnDrop(null);
      },
      onDrop: async ({ source }) => {
        if (!userIsPermittedToAssignSchedule) {
          setOnDrop(null);
          alert("You are not permitted to assign schedules");
          return;
        }
        const { tourGroupId } = source.data as unknown as DragSourceData;
        if (onDrop === "allow") {
          await addOrDeleteVehicleIdToTourGroup({
            tour_group_id: tourGroupId,
            vehicle_id: vehicle_id,
          });

          const newSchedule = await getScheduleOfVehicleByDate({
            vehicle_id: vehicle_id,
            date: calendarDate,
          });

          if (newSchedule?.data) {
            setScheduleData(newSchedule.data);
          }

          await queryClient.invalidateQueries({
            queryKey: ["TOUR_GROUP_FOR_SCHEDULE_BY_ID", tourGroupId],
          });
        }
        setOnDrop(null);
      },
    });
  }, [
    calendarDate,
    user,
    userIsPermittedToAssignSchedule,
    scheduleData,
    vehicle_id,
    onDrop,
    checkIfVehicleHasAlreadyAssignedThisTourGroup,
    queryClient,
    addOrDeleteVehicleIdToTourGroup,
    entities,
    getScheduleOfVehicleByDate,
  ]);

  //-----------------------------------------------------------

  return (
    <>
      <Container
        ref={containerRef}
        onClick={() => {
          //setOpenDrawer(true);
        }}
      >
        {isPending && (
          <SpinnerContainer>
            <div
              style={{
                color: "grey",
                width: "15px",
                height: "15px",
              }}
              className="spinner-border"
              role="status"
            ></div>
          </SpinnerContainer>
        )}

        {shouldShowPlusIcon && !onDrop && (
          <AddCircleIcon
            color="success"
            sx={{
              margin: "auto",
            }}
          />
        )}

        {onDrop && (
          <Overlay allowDrop={onDrop === "allow"}>
            {onDrop === "allow" ? (
              <MoveToInboxIcon color="success" />
            ) : (
              <BlockIcon color="error" />
            )}
          </Overlay>
        )}

        {hasScheduleData &&
          scheduleData.map((tourGroup) => {
            return (
              <ScheduleItem key={uuidv4()} tourGroup={tourGroup as TourGroup} />
            );
          })}
      </Container>
      {/* <Drawer open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <ScheduleAssignmentDrawerContent
          user={userData}
          date={tourDate}
          scheduleData={scheduleData}
          addOrUpdateUserDaySchedule={addOrUpdateUserDaySchedule}
          isUpdating={isUpdating}
          closeDrawer={() => setOpenDrawer(false)}
        />
      </Drawer> */}
    </>
  );
};

export default CalendarBox;
